import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  HeartIcon,
  ServerIcon,
  UserGroupIcon,
  RocketLaunchIcon,
  SpeakerWaveIcon,
  LightBulbIcon,
  PencilSquareIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';

const SupportProject: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Monthly costs data removed per request

  const benefits = [
    {
      icon: RocketLaunchIcon,
      title: 'Powerful New Features',
      description: 'Your support accelerates development of advanced PDF tools, AI enhancements, and innovative features'
    },
    {
      icon: LightBulbIcon,
      title: 'Always Free Forever',
      description: 'Help us maintain zero-cost access for everyone - no subscriptions, no premium tiers, no limits'
    },
    {
      icon: ServerIcon,
      title: 'Better Performance',
      description: 'Faster processing, higher quality results, and improved reliability for all users'
    },
    {
      icon: UserGroupIcon,
      title: 'Growing Community',
      description: 'Support helps us reach more students and professionals who need free PDF tools'
    }
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-r from-primary to-secondary p-4 rounded-full">
              <HeartIcon className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-4">
            Support <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">NotesCrafter</span>
          </h1>
          <p className="text-xl text-text-muted max-w-2xl mx-auto">
            Help us bring powerful new features and keep NotesCrafter free forever for everyone
          </p>
        </motion.div>

        {/* Support Options - Moved to top */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-2xl p-8 md:p-12 mb-12"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-text-main mb-3">Support NotesCrafter</h2>
            <p className="text-text-muted text-lg">
              Every contribution, no matter how small, makes a big difference
            </p>
          </div>
          
          {/* UPI Section - Simplified */}
          <div className="max-w-2xl mx-auto mb-10">
            <h3 className="text-xl font-semibold text-text-main mb-6 text-center">UPI Payment (India)</h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              {/* QR Code */}
              <div className="flex-shrink-0">
                <div className="bg-white rounded-2xl p-4 shadow-xl">
                  <img 
                    src="/notescrafterqr.webp" 
                    alt="UPI QR Code" 
                    className="w-48 h-48 object-contain"
                  />
                </div>
                <p className="text-sm text-text-muted text-center mt-3">Scan with any UPI app</p>
              </div>

              {/* Message Option */}
              <div className="flex-1 text-center md:text-left">
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 text-primary mb-3">
                    <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
                    <span className="font-semibold">Want to send a message?</span>
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed">
                    We love hearing from you! Use the button below to add a personal note with your contribution.
                  </p>
                </div>
                
                <a
                  href="https://razorpay.me/@notescrafter"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <HeartIcon className="w-5 h-5" />
                  <span>Support with Message</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="relative my-10">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-glow"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-4 text-sm text-text-muted">or choose another method</span>
            </div>
          </div>

          {/* Other Payment Methods - Simplified */}
          <div className="max-w-2xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Buy Me a Coffee */}
              <a 
                href="https://www.buymeacoffee.com/notescrafter" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="bg-white/20 p-2.5 rounded-lg">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.216 6.415l-.132-.666c-.119-.598-.388-1.163-1.001-1.379-.197-.069-.42-.098-.57-.241-.152-.143-.196-.366-.231-.572-.065-.378-.125-.756-.192-1.133-.057-.325-.102-.69-.25-.987-.195-.4-.597-.634-.996-.788a5.723 5.723 0 00-.626-.194c-1-.263-2.05-.36-3.077-.416a25.834 25.834 0 00-3.7.062c-.915.083-1.88.184-2.75.5-.318.116-.646.256-.888.501-.297.302-.393.77-.177 1.146.154.267.415.456.692.58.36.162.737.284 1.123.366 1.075.238 2.189.331 3.287.37 1.218.05 2.437.01 3.65-.118.299-.033.598-.073.896-.119.352-.054.578-.513.474-.834-.124-.383-.457-.531-.834-.473-.466.074-.96.108-1.382.146-1.177.08-2.358.082-3.536.006a22.228 22.228 0 01-1.157-.107c-.086-.01-.18-.025-.258-.036-.243-.036-.484-.08-.724-.13-.111-.027-.111-.185 0-.212h.005c.277-.06.557-.108.838-.147h.002c.131-.009.263-.032.394-.048a25.076 25.076 0 013.426-.12c.674.019 1.347.067 2.017.144l.228.031c.267.04.533.088.798.145.392.085.895.113 1.07.542.055.137.08.288.111.431l.319 1.484a.237.237 0 01-.199.284h-.003c-.037.006-.075.01-.112.015a36.704 36.704 0 01-4.743.295 37.059 37.059 0 01-4.699-.304c-.14-.017-.293-.042-.417-.06-.326-.048-.649-.108-.973-.161-.393-.065-.768-.032-1.123.161-.29.16-.527.404-.675.701-.154.316-.199.66-.267 1-.069.34-.176.707-.135 1.056.087.753.613 1.365 1.37 1.502a39.69 39.69 0 0011.343.376.483.483 0 01.535.53l-.071.697-1.018 9.907c-.041.41-.047.832-.125 1.237-.122.637-.553 1.028-1.182 1.171-.577.131-1.165.2-1.756.205-.656.004-1.31-.025-1.964-.06-.356-.02-.71-.035-1.064-.035-.588 0-1.141-.036-1.694-.177a2.153 2.153 0 01-1.408-1.034 3.096 3.096 0 01-.311-.824c-.062-.291-.098-.583-.128-.877l-.077-.75c-.023-.229.111-.373.338-.373h2.523c.234 0 .375.138.352.366l-.059.573a1.832 1.832 0 00.018.533c.063.371.383.594.75.611.763.035 1.527.05 2.291.011.413-.02.826-.065 1.239-.112.211-.024.433-.032.639-.08.452-.105.69-.408.728-.862l.024-.239c.006-.057.01-.114.015-.171l.025-.282 1.04-10.153a.336.336 0 00-.326-.386 38.04 38.04 0 01-11.334-.378c-.69-.142-1.116-.64-1.24-1.331-.065-.344-.03-.696.053-1.037.079-.33.159-.663.261-.986a2.06 2.06 0 01.644-.955c.326-.271.713-.453 1.123-.579.469-.144.957-.221 1.449-.24a37.38 37.38 0 014.912.265c.093.013.186.025.279.035a36.698 36.698 0 004.743-.295h.003a.853.853 0 00.717-.719l.319-1.484a2.118 2.118 0 00-.111-.431c-.175-.429-.678-.457-1.07-.542z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-bold">Buy Me a Coffee</p>
                  <p className="text-xs text-white/80">Quick contribution</p>
                </div>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>

              {/* Razorpay */}
              <a 
                href="https://razorpay.me/@notescrafter" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white p-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="bg-white/20 p-2.5 rounded-lg">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.72 8.79l-4.29 4.3-2.12-2.12-1.42 1.42 3.54 3.54 5.71-5.72-1.42-1.42zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-bold">Razorpay</p>
                  <p className="text-xs text-white/80">Cards, UPI, Wallets</p>
                </div>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>

          {/* Alternative Ways */}
          <div className="mt-10 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-text-main mb-4 text-center">Other Ways to Help</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start gap-3">
                <SpeakerWaveIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-text-muted">Share with friends and colleagues</span>
              </div>
              <div className="flex items-start gap-3">
                <LightBulbIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-text-muted">Provide feedback and suggestions</span>
              </div>
              <div className="flex items-start gap-3">
                <PencilSquareIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-text-muted">Write a review or testimonial</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Why Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-2xl p-8 md:p-12 mb-12 hover:border-primary/50 transition-colors"
        >
          <h2 className="text-3xl font-bold text-text-main mb-6 text-center">Why Your Support Matters</h2>
          <p className="text-lg text-text-muted leading-relaxed mb-6">
            NotesCrafter is committed to being the best free PDF processing tool available. Your support directly funds 
            the development of powerful new features like advanced AI enhancements, batch processing, cloud sync, 
            and innovative PDF tools that push the boundaries of what's possible in the browser.
          </p>
          <p className="text-lg text-text-muted leading-relaxed">
            Most importantly, your contributions ensure NotesCrafter remains completely free forever - no subscriptions, 
            no premium features locked behind paywalls, no credit systems. Every user gets access to everything, always. 
            Help us sustain this vision and bring even more powerful capabilities to users worldwide.
          </p>
        </motion.div>

  {/* Removed monthly costs and contribution CTA per request */}

        {/* What Your Support Enables */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-text-main mb-6 text-center">What Your Support Enables</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-xl p-6 hover:border-primary/50 hover:shadow-xl transition-all">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-primary to-secondary p-3 rounded-lg flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-text-main mb-2">{benefit.title}</h3>
                      <p className="text-text-muted">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Thank You Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-2xl p-8 md:p-12 text-center hover:border-primary/50 transition-colors"
        >
          <HeartIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-text-main mb-4">Thank You!</h2>
          <p className="text-lg text-text-muted leading-relaxed max-w-2xl mx-auto">
            Your support powers the development of powerful new features and ensures NotesCrafter remains free forever. 
            Whether you contribute financially, spread the word, or provide feedback, you're helping us build the most 
            capable free PDF processing tool available. Together, we're creating something truly special!
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SupportProject;
